import { useState, useEffect } from "react"
import Sidebar from "@/components/Sidebar"
import { useAuth } from "@/contexts/AuthContext"
import { authService, userService } from "@/api/services"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { Check, Loader2, User, Lock, Eye, EyeOff, Save } from "lucide-react"

export default function SettingsPage() {
  const { user, updateUser } = useAuth()
  
  // Account details form state
  const [accountFormData, setAccountFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    address: "",
    city: "",
  })
  const [accountError, setAccountError] = useState("")
  const [accountSuccess, setAccountSuccess] = useState("")
  const [isSavingAccount, setIsSavingAccount] = useState(false)

  // Password form state
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [repeatPassword, setRepeatPassword] = useState("")
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showRepeatPassword, setShowRepeatPassword] = useState(false)
  const [passwordError, setPasswordError] = useState("")
  const [passwordSuccess, setPasswordSuccess] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form data from user
  useEffect(() => {
    if (user) {
      setAccountFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phoneNumber: user.phoneNumber || "",
        address: user.address || "",
        city: user.city || "",
      })
    }
  }, [user])

  const handleAccountUpdate = async () => {
    if (!user?.id) return

    setAccountError("")
    setAccountSuccess("")
    setIsSavingAccount(true)

    try {
      const updatedUser = await userService.updateUser(user.id, accountFormData)
      
      // Update user in context and localStorage
      updateUser(updatedUser)
      
      setAccountSuccess("Account updated successfully")
      
      // Clear success message after 3 seconds
      setTimeout(() => setAccountSuccess(""), 3000)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update account"
      setAccountError(errorMessage)
    } finally {
      setIsSavingAccount(false)
    }
  }

  const handlePasswordChange = async () => {
    setPasswordError("")
    setPasswordSuccess("")

    // Validation
    if (!currentPassword || !newPassword || !repeatPassword) {
      setPasswordError("Please fill in all fields")
      return
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters")
      return
    }

    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/
    if (!passwordPattern.test(newPassword)) {
      setPasswordError("Password must contain uppercase, lowercase, and number")
      return
    }

    if (newPassword !== repeatPassword) {
      setPasswordError("Passwords do not match")
      return
    }

    setIsSubmitting(true)

    try {
      const response = await authService.changePassword({
        currentPassword,
        newPassword,
        repeatPassword,
      })
      
      setPasswordSuccess(response.message || "Password updated successfully")
      setCurrentPassword("")
      setNewPassword("")
      setRepeatPassword("")
      setShowCurrentPassword(false)
      setShowNewPassword(false)
      setShowRepeatPassword(false)
      
      // Clear success message after 3 seconds
      setTimeout(() => setPasswordSuccess(""), 3000)
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || err.message || "Failed to change password")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-2xl">
          <h1 className="text-3xl font-bold text-foreground mb-2">Account Settings</h1>
          <p className="text-muted-foreground mb-8">Manage your account information</p>

          {/* Account Details Section */}
          <Card className="p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Account Details</h2>
                <p className="text-sm text-muted-foreground">Your personal information</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={accountFormData.firstName}
                    onChange={(e) =>
                      setAccountFormData({ ...accountFormData, firstName: e.target.value })
                    }
                    disabled={isSavingAccount}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={accountFormData.lastName}
                    onChange={(e) =>
                      setAccountFormData({ ...accountFormData, lastName: e.target.value })
                    }
                    disabled={isSavingAccount}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={accountFormData.phoneNumber}
                  onChange={(e) =>
                    setAccountFormData({ ...accountFormData, phoneNumber: e.target.value })
                  }
                  disabled={isSavingAccount}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={accountFormData.address}
                  onChange={(e) =>
                    setAccountFormData({ ...accountFormData, address: e.target.value })
                  }
                  disabled={isSavingAccount}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={accountFormData.city}
                  onChange={(e) =>
                    setAccountFormData({ ...accountFormData, city: e.target.value })
                  }
                  disabled={isSavingAccount}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="userType">User Type</Label>
                <Input
                  id="userType"
                  value={user?.userType || ""}
                  disabled
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">User type cannot be changed</p>
              </div>

              {accountError && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <p className="text-sm text-destructive">{accountError}</p>
                </div>
              )}

              {accountSuccess && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-success" />
                  <p className="text-sm text-success">{accountSuccess}</p>
                </div>
              )}

              <Button
                onClick={handleAccountUpdate}
                disabled={isSavingAccount}
                className="w-full"
              >
                {isSavingAccount ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Password Section */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Change Password</h2>
                <p className="text-sm text-muted-foreground">Update your password</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    disabled={isSubmitting}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    disabled={isSubmitting}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Must be at least 8 characters and include uppercase, lowercase, and a number
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="repeatPassword">Repeat New Password</Label>
                <div className="relative">
                  <Input
                    id="repeatPassword"
                    type={showRepeatPassword ? "text" : "password"}
                    value={repeatPassword}
                    onChange={(e) => setRepeatPassword(e.target.value)}
                    placeholder="Repeat new password"
                    disabled={isSubmitting}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showRepeatPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {passwordError && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <p className="text-sm text-destructive">{passwordError}</p>
                </div>
              )}

              {passwordSuccess && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-success" />
                  <p className="text-sm text-success">{passwordSuccess}</p>
                </div>
              )}

              <Button 
                onClick={handlePasswordChange} 
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </div>
          </Card>
        </div>
      </main>
    </div>
  )
}
